import { isVersionAtLeast } from '@/app/lib/versions';

/**
 * Shared rendering of Minecraft text components across version eras.
 *
 * Minecraft 1.21.5 stopped storing text components as JSON wrapped in a string
 * and inlined them as SNBT instead:
 *
 *   <  1.21.5   custom_name='{"text":"Hi","italic":false}'   (quoted JSON string)
 *   >= 1.21.5   custom_name={text:"Hi",italic:false}          (bare SNBT)
 *
 * Emitting the old form on 1.21.5+ doesn't error — the client renders the raw
 * JSON literally, so users see `{"text":"Hi"}` as the item name / book page /
 * mob nameplate. Because the app's DEFAULT version is 1.21.11, getting this
 * wrong breaks output for nearly every user.
 *
 * Every generator that emits a name, lore, page or chat component MUST go
 * through here rather than hand-rolling the wrapping — hand-rolling is exactly
 * how the same bug independently appeared in ten separate tools.
 */

export type TextComponent = Record<string, unknown> | string;

/** True when text components are bare SNBT rather than JSON-in-a-string. */
export function usesBareSnbtText(version: string): boolean {
  return isVersionAtLeast(version, '1.21.5');
}

/** Keys renamed in 1.21.5. Applied recursively when rendering for that era. */
const RENAMED_IN_1_21_5: Record<string, string> = {
  hoverEvent: 'hover_event',
  clickEvent: 'click_event',
};

/**
 * 1.21.5 restructured the hover_event object itself, not just its key. The
 * pre-1.21.5 `contents`-wrapped shape is inlined and some inner fields renamed
 * (verified against the 1.21.5 changelog on minecraft.wiki):
 *   show_text:   {action, contents:<comp>}            → {action, value:<comp>}
 *   show_item:   {action, contents:{id,count,components}} → {action, id, count, components}
 *   show_entity: {action, contents:{type, id, name}}   → {action, id:<type>, uuid:<id>, name}
 * The renameKeys pass (1.21.5+ only) applies this to any hoverEvent/hover_event
 * value that still carries a `contents` wrapper; values already in the new shape
 * (no `contents`) pass through untouched, so re-rendering is idempotent.
 */
function transformHoverForBareSnbt(hover: unknown): unknown {
  if (!hover || typeof hover !== 'object' || Array.isArray(hover)) return hover;
  const obj = hover as Record<string, unknown>;
  if (!('contents' in obj)) return hover; // already new-shape (or nothing to inline)
  const action = obj.action;
  const contents = obj.contents;

  if (action === 'show_text') {
    return { action, value: contents };
  }
  if (action === 'show_item') {
    if (contents && typeof contents === 'object' && !Array.isArray(contents)) {
      return { action, ...(contents as Record<string, unknown>) };
    }
    return { action, id: contents }; // contents given as a bare item id string
  }
  if (action === 'show_entity') {
    const c = (contents && typeof contents === 'object' && !Array.isArray(contents))
      ? (contents as Record<string, unknown>)
      : {};
    const out: Record<string, unknown> = { action };
    if (c.type !== undefined) out.id = c.type;   // entity type: `type` → `id`
    if (c.id !== undefined) out.uuid = c.id;      // entity UUID: `id` → `uuid`
    if (c.name !== undefined) out.name = c.name;
    return out;
  }
  return hover;
}

/**
 * 1.21.5 also restructured click_event (verified against the same changelog),
 * moving the generic `value` field to a per-action field:
 *   open_url:          value → url
 *   run_command:       value → command   (leading `/` no longer required)
 *   suggest_command:   value → command
 *   change_page:       value (string) → page (positive integer)
 *   copy_to_clipboard: value            (unchanged)
 * Applied to any clickEvent/click_event value that still carries a `value` key;
 * new-shape values (no `value`) pass through, so the transform is idempotent.
 */
function transformClickForBareSnbt(click: unknown): unknown {
  if (!click || typeof click !== 'object' || Array.isArray(click)) return click;
  const obj = click as Record<string, unknown>;
  if (!('value' in obj)) return click;
  const action = obj.action;
  const value = obj.value;
  switch (action) {
    case 'open_url':
      return { action, url: value };
    case 'run_command':
    case 'suggest_command':
      return { action, command: value };
    case 'change_page': {
      const n = typeof value === 'number' ? value : Number.parseInt(String(value), 10);
      return { action, page: Number.isNaN(n) ? value : n };
    }
    default: // copy_to_clipboard and any unknown action keep {action, value}
      return click;
  }
}

function renameKeys(value: unknown): unknown {
  if (value instanceof RawSnbt) return value; // opaque fragment — never destructure
  if (Array.isArray(value)) return value.map(renameKeys);
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      if (k === 'hoverEvent' || k === 'hover_event') {
        out.hover_event = renameKeys(transformHoverForBareSnbt(v));
      } else if (k === 'clickEvent' || k === 'click_event') {
        out.click_event = renameKeys(transformClickForBareSnbt(v));
      } else {
        out[RENAMED_IN_1_21_5[k] ?? k] = renameKeys(v);
      }
    }
    return out;
  }
  return value;
}

/**
 * A pre-formatted SNBT fragment that is emitted VERBATIM (not quoted/escaped).
 * Used for raw user-supplied SNBT that must keep full NBT fidelity — e.g. a
 * show_item `components` compound that uses SNBT-only types (`5b`, `[I;…]`)
 * which can't survive a JSON round-trip. Only meaningful on the ≥1.21.5 (bare
 * SNBT) path; the caller is responsible for having validated it with
 * `isValidSnbt` first.
 */
export class RawSnbt {
  readonly raw: string;
  constructor(raw: string) {
    this.raw = raw;
  }
}
export function rawSnbt(raw: string): RawSnbt {
  return new RawSnbt(raw);
}

const SNBT_ESCAPES: Record<string, string> = {
  '\\': '\\\\', '"': '\\"', '\n': '\\n', '\t': '\\t', '\r': '\\r', '\b': '\\b', '\f': '\\f',
};

/**
 * A double-quoted string for the 1.21.5+ SNBT grammar ONLY. That grammar (SnbtGrammar, read from
 * 26.2's bytecode) has `\n \t \r \b \f \s \' \"` and `\x## \u#### \U########` escapes; before
 * 1.21.5 only `\\` and the quote existed, so never use this on an older version. A raw newline
 * from a multi-line text box used to reach the game unescaped. `§` is escaped too: chat refuses it
 * as a typed character, and `§` reads back as the same character.
 */
export function snbtQuotedString(value: string): string {
  return `"${value.replace(/[\\"\u0000-\u001f\u007f§]/g,
    c => SNBT_ESCAPES[c] ?? `\\u${c.charCodeAt(0).toString(16).padStart(4, '0')}`)}"`;
}

function snbtValue(value: unknown): string {
  if (value instanceof RawSnbt) return value.raw;
  if (typeof value === 'string') return snbtQuotedString(value);
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (typeof value === 'number') return String(value);
  if (Array.isArray(value)) return `[${value.map(snbtValue).join(',')}]`;
  // null has typeof 'object' but is falsy — it (and undefined) has no SNBT form;
  // compounds drop such keys (see snbtCompound), so this is only a stray-array-
  // element fallback.
  if (value === null || value === undefined) return '""';
  if (typeof value === 'object') {
    return snbtCompound(value as Record<string, unknown>);
  }
  return '""';
}

// An SNBT key can be written bare only if it's a run of these chars; anything
// else (notably a namespaced `minecraft:damage` component key, whose `:` is the
// key/value separator) MUST be quoted or the compound fails to parse.
const BARE_SNBT_KEY = /^[A-Za-z0-9_+.-]+$/;

function snbtKey(key: string): string {
  return BARE_SNBT_KEY.test(key) ? key : `"${key.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`;
}

/** Render a plain object as SNBT: bare-or-quoted keys, quoted+escaped string values. */
export function snbtCompound(obj: Record<string, unknown>): string {
  const parts = Object.entries(obj)
    // Drop both undefined AND null — SNBT has no null type, and `key:""` (the old
    // fallback) is an invalid value the game rejects. `!= null` catches both.
    .filter(([, v]) => v != null)
    .map(([k, v]) => `${snbtKey(k)}:${snbtValue(v)}`);
  return `{${parts.join(',')}}`;
}

/**
 * Render an arbitrary text-component VALUE — a single component object, or
 * the sibling-array form `["", {...}, {...}]` used when multiple components
 * are joined (e.g. tellraw/book rich-mode output) — for embedding as an NBT
 * string value, correct for `version`.
 *
 * Pre-1.21.5 the result INCLUDES its surrounding single quotes (it is a quoted
 * JSON string); from 1.21.5 it is a bare SNBT value with no quotes. Callers
 * therefore interpolate the result directly and must NOT add quotes themselves.
 *
 * Use this (rather than `renderTextComponent`) whenever the value being
 * rendered might be the sibling-array form rather than a single object.
 */
export function renderTextValue(value: unknown, version: string): string {
  if (usesBareSnbtText(version)) {
    return snbtValue(renameKeys(value));
  }
  // Single-quoted JSON string — escape any single quote and backslash so the
  // surrounding SNBT string stays valid.
  const json = JSON.stringify(value);
  return `'${json.replace(/\\/g, '\\\\').replace(/'/g, "\\'")}'`;
}

/**
 * Render a text component for embedding in a command, correct for `version`.
 *
 * Pre-1.21.5 the result INCLUDES its surrounding single quotes (it is a quoted
 * JSON string); from 1.21.5 it is a bare SNBT compound with no quotes. Callers
 * therefore interpolate the result directly and must NOT add quotes themselves.
 */
export function renderTextComponent(component: TextComponent, version: string): string {
  return renderTextValue(component, version);
}

/** Render a list of text components (lore, book pages) for `version`. */
export function renderTextComponentList(components: TextComponent[], version: string): string {
  return `[${components.map(c => renderTextComponent(c, version)).join(',')}]`;
}

/**
 * Render a text-component value for use as a RAW COMMAND ARGUMENT — e.g.
 * `/tellraw <target> <component>` — where the value is parsed directly by
 * the command's own component-argument grammar and is NOT embedded inside a
 * surrounding NBT string. No quotes are added in either era:
 *
 *   <  1.21.5   {"text":"Hi"}   (raw JSON — the legacy component argument
 *                                grammar parses JSON directly)
 *   >= 1.21.5   {text:"Hi"}     (bare SNBT)
 *
 * Do NOT use `renderTextComponent`/`renderTextValue` for this — those always
 * add surrounding quotes pre-1.21.5, which is correct for a component nested
 * inside another NBT compound (sign messages, book `raw:` pages) but wrong
 * for a bare top-level command argument.
 */
export function renderComponentArgument(value: unknown, version: string): string {
  if (usesBareSnbtText(version)) {
    return snbtValue(renameKeys(value));
  }
  return JSON.stringify(value);
}

/**
 * Render the `minecraft:enchantments` / `minecraft:stored_enchantments` VALUE.
 *
 * Two forms exist:
 *   1.20.5 – 1.21.4   {levels:{sharpness:5}}   (the original wrapper form)
 *   >= 1.21.5         {sharpness:5}            (wrapper removed — ONLY form)
 *
 * Nuance worth knowing: the wiki's 24w10a entry says these components "can now
 * optionally be defined in simpler formats", i.e. the flat form is ALSO accepted
 * from 1.20.5 — so both would work in the middle range. We deliberately emit the
 * `levels:` wrapper there anyway, because it is unambiguously valid under either
 * reading and keeps output byte-identical to what the app produced before. The
 * flat form is used only from 1.21.5, where the wrapper is genuinely invalid.
 *
 * Bare SNBT keys cannot contain `:`, so namespaced (modded) ids are quoted.
 */
export function renderEnchantmentsMap(
  entries: Array<{ id: string; level: number }>,
  version: string,
): string {
  const parts = entries.map(({ id, level }) => {
    const bare = id.startsWith('minecraft:') ? id.slice('minecraft:'.length) : id;
    const key = bare.includes(':') ? `"${bare}"` : bare;
    return `${key}:${level}`;
  });
  const map = `{${parts.join(',')}}`;
  return usesBareSnbtText(version) ? map : `{levels:${map}}`;
}
